
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { clientFormSchema, type ClientFormValues } from "./schema/form-schema";
import { useClientFormSubmit } from "./hooks/useClientFormSubmit";
import { Lock } from "lucide-react";

interface ClientFormProps {
  onSuccess: () => void;
  initialValues?: Partial<ClientFormValues>;
  mode?: 'create' | 'edit';
  clientId?: string;
}

export const ClientForm = ({ 
  onSuccess, 
  initialValues, 
  mode = 'create', 
  clientId 
}: ClientFormProps) => {
  const form = useForm<ClientFormValues>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      school_id: initialValues?.school_id || "",
      nama_sekolah: initialValues?.nama_sekolah || "",
    },
  });

  const { handleSubmit, isLoading, isSchoolIdEditable } = useClientFormSubmit({
    mode,
    clientId,
    onSuccess,
  });

  const onSubmit = async (data: ClientFormValues) => {
    const success = await handleSubmit(data);
    if (success) {
      form.reset();
      onSuccess();
    }
  };

  const handleNumberOnlyInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    e.target.value = value;
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="school_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                School ID
                {mode === 'edit' && !isSchoolIdEditable && (
                  <Lock className="h-4 w-4 text-gray-500" />
                )}
              </FormLabel>
              <FormControl>
                <Input 
                  placeholder="Enter School ID" 
                  {...field}
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  onInput={handleNumberOnlyInput}
                  disabled={mode === 'edit' && !isSchoolIdEditable}
                  className={mode === 'edit' && !isSchoolIdEditable ? 'bg-gray-100' : ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="nama_sekolah"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nama Sekolah</FormLabel>
              <FormControl>
                <Input placeholder="Enter school name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? "Loading..." : mode === 'create' ? "Create Client" : "Update Client"}
        </Button>
      </form>
    </Form>
  );
};
